/**
 * Traces of any substantial compilation or elaboration process are usually extremely verbose,
 * which makes them slow (or even infeasible) to pretty-print and difficult to understand.
 * Instead, we provide a "TraceExplorer" UI which allows users to lazily expand trace subtrees,
 * and (TODO) execute search queries.
 *
 * @module
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { Goal } from './goals';
import { InteractiveCode, InteractiveTaggedText } from './interactiveCode';
import { InteractiveDiagnostics_msgToInteractive, lazyTraceChildrenToInteractive } from '@leanprover/infoview-api';
import { mapRpcError, useAsyncWithTrigger } from './util';
import { RpcContext } from './rpcSessions';
function LazyTrace(_a) {
    var col = _a.col, cls = _a.cls, msg = _a.msg;
    var rs = React.useContext(RpcContext);
    var _b = __read(useAsyncWithTrigger(function () {
        return InteractiveDiagnostics_msgToInteractive(rs, msg, col);
    }, [rs, msg, col]), 2), tt = _b[0], fetchTrace = _b[1];
    var _c = __read(React.useState(false), 2), open = _c[0], setOpen = _c[1];
    if (!open)
        return React.createElement("span", { className: "underline-hover pointer", onClick: function (ev) {
                void fetchTrace();
                setOpen(true);
                ev.stopPropagation();
            } },
            "[",
            cls,
            "] >");
    else if (tt.state === 'resolved')
        return React.createElement(React.Fragment, null,
            React.createElement("span", { className: "underline-hover pointer", onClick: function (ev) {
                    setOpen(false);
                    ev.stopPropagation();
                } },
                "[",
                cls,
                "] \u2228"),
            React.createElement(InteractiveMessage, { fmt: tt.value }));
    else if (tt.state === 'rejected')
        return React.createElement(React.Fragment, null,
            React.createElement("span", { className: "underline-hover pointer", onClick: function (ev) {
                    void fetchTrace();
                    ev.stopPropagation();
                } },
                "[",
                cls,
                "] Error (click to retry):"),
            " ",
            mapRpcError(tt.error));
    else
        return React.createElement("span", null,
            "[",
            cls,
            "] Loading..");
}
var TraceClassContext = React.createContext('');
function abbreviateCommonPrefix(parent, cls) {
    var parentParts = parent.split('.');
    var clsParts = cls.split('.');
    var i = 0;
    for (; i < parentParts.length && i < clsParts.length && parentParts[i] === clsParts[i]; i++)
        ;
    return clsParts.slice(i).join('.');
}
function TraceLine(_a) {
    var indent = _a.indent, cls = _a.cls, msg = _a.msg, icon = _a.icon;
    var spaces = ' '.repeat(indent);
    var abbrCls = abbreviateCommonPrefix(React.useContext(TraceClassContext), cls);
    return React.createElement("div", { className: 'trace-line' },
        spaces,
        React.createElement("span", { className: "trace-class", title: cls },
            "[",
            abbrCls,
            "]"),
        " ",
        React.createElement(InteractiveMessage, { fmt: msg }),
        " ",
        icon);
}
function ChildlessTraceNode(traceEmbed) {
    return React.createElement(TraceLine, __assign({}, traceEmbed, { icon: '' }));
}
function CollapsibleTraceNode(traceEmbed) {
    var _this = this;
    var cls = traceEmbed.cls, collapsedByDefault = traceEmbed.collapsed, lazyKids = traceEmbed.children;
    var rs = React.useContext(RpcContext);
    var _a = __read(useAsyncWithTrigger(function () { return __awaiter(_this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            if ('strict' in lazyKids) {
                return [2 /*return*/, lazyKids.strict];
            }
            else {
                return [2 /*return*/, lazyTraceChildrenToInteractive(rs, lazyKids.lazy)];
            }
            return [2 /*return*/];
        });
    }); }, [rs, lazyKids]), 2), children = _a[0], fetchChildren = _a[1];
    var _b = __read(React.useState(!collapsedByDefault), 2), open = _b[0], setOpen = _b[1]; // TODO: reset when collapsedByDefault changes?
    if (open && children.state === 'notStarted')
        void fetchChildren();
    var icon = open ? '▼' : '▶';
    if (children.state === 'loading')
        icon += ' ⋯';
    var onClick = React.useCallback(function (ev) {
        ev.stopPropagation();
        if (!open)
            void fetchChildren();
        setOpen(function (o) { return !o; });
    }, [open]);
    return React.createElement("div", null,
        React.createElement("div", { className: 'pointer', onClick: onClick },
            React.createElement(TraceLine, __assign({}, traceEmbed, { icon: icon }))),
        React.createElement("div", { style: { display: open ? 'block' : 'none' } },
            React.createElement(TraceClassContext.Provider, { value: cls }, children.state === 'resolved' ? children.value.map(function (tt, i) { return React.createElement(InteractiveMessage, { fmt: tt, key: i }); })
                : children.state === 'rejected' ? mapRpcError(children.error).toString()
                    : React.createElement(React.Fragment, null))));
}
function Trace(traceEmbed) {
    var noChildren = 'strict' in traceEmbed.children && traceEmbed.children.strict.length === 0;
    return noChildren ? React.createElement(ChildlessTraceNode, __assign({}, traceEmbed)) : React.createElement(CollapsibleTraceNode, __assign({}, traceEmbed));
}
function InteractiveMessageTag(_a) {
    var embed = _a.tag;
    if ('expr' in embed)
        return React.createElement(InteractiveCode, { fmt: embed.expr });
    else if ('goal' in embed)
        return React.createElement(Goal, { goal: embed.goal, filter: { reverse: false, showType: false, showInstance: false, showHiddenAssumption: false, showLetValue: false } });
    else if ('lazyTrace' in embed)
        return React.createElement(LazyTrace, { col: embed.lazyTrace[0], cls: embed.lazyTrace[1], msg: embed.lazyTrace[2] });
    else if ('trace' in embed)
        return React.createElement(Trace, __assign({}, embed.trace));
    else
        return React.createElement("div", null,
            "malformed MsgEmbed: ",
            JSON.stringify(embed));
}
export function InteractiveMessage(_a) {
    var fmt = _a.fmt;
    return InteractiveTaggedText({ fmt: fmt, InnerTagUi: InteractiveMessageTag });
}
